import type React from "react";
import {PaymentsOutlined, ReceiptLongOutlined, SavingsOutlined} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag";
import {SitKpiHeader} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import type {PtV2LeakageRow} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    daysLostLabel,
    formatEur,
    formatEurFull,
    formatInt,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2SourcesQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2Citation} from "~/experiments/pages/payment-terms-v2/components/PtV2Citation.tsx";

const LEAKAGE_SLUGS = ['payment_term_leakage_register', 'pt_contract_leakage'];

/** Drawer body for one payment-term-leakage supplier: the gap, the calculation and the fix. */
export const PtV2LeakageDetail: React.FC<{row: PtV2LeakageRow}> = ({row}) => {
    const sourcesQ = usePtV2SourcesQuery();
    const tables = sourcesQ.data?.workpapers.find(w => LEAKAGE_SLUGS.includes(w.slug))?.sourceTables ?? [];
    const lost = daysLostLabel(row);
    return <div className="ptv2-detail">
        <div className="ptv2-chips">
            <Tag color="success" label={`Contract ${row.contractTerm}`}/>
            <Tag color="warning" label={`ERP ${row.erpTerm}`}/>
            <Tag color="error" variant="outlined" label={`${lost} days early`}/>
        </div>
        <SitKpiHeader items={[
            {tone: 'ok', icon: <SavingsOutlined/>, label: 'Trapped', value: formatEur(row.wcTrapped)},
            {tone: 'plain', icon: <PaymentsOutlined/>, label: 'Spend', value: formatEur(row.spend)},
            {tone: 'info', icon: <ReceiptLongOutlined/>, label: 'Invoices', value: formatInt(row.invoiceCount)},
        ]}/>
        <p>
            The signed contract allows {row.contractTerm}, but the ERP processes this supplier’s invoices
            on {row.erpTerm}. Setting the vendor master default to <b>{row.recommendedCode}</b> releases{' '}
            {formatEurFull(row.wcTrapped)} without any negotiation.
        </p>
        <dl className="ptv2-kv">
            <dt>Recommended SAP code</dt><dd><b className="ptv2-mono">{row.recommendedCode}</b></dd>
            {row.recommendedAction && <><dt>Recommended action</dt><dd>{row.recommendedAction}</dd></>}
            <dt>Spend</dt><dd className="ptv2-num">{formatEurFull(row.spend)}</dd>
            <dt>Calculation</dt>
            <dd className="ptv2-mono">{formatEurFull(row.spend)} × {lost} ÷ 365 = {formatEurFull(row.wcTrapped)}</dd>
            {row.supplierId && <><dt>ERP vendor id</dt><dd className="ptv2-mono">{row.supplierId}</dd></>}
            <dt>Sources</dt>
            <dd className="ptv2-chips">
                {tables.length ? tables.map(t => <PtV2Citation key={t}>{t}</PtV2Citation>) : <span className="ptv2-muted">{LEAKAGE_SLUGS[0]}</span>}
            </dd>
        </dl>
    </div>;
};
